/**
 * CIO landing page — /app/landing/cio
 *
 * Strategic IT leadership, business-aligned. Distinct from CTO
 * (technical leadership): the CIO cares about portfolio, vendors, cost,
 * and operational resilience. Hero tiles emphasise SaaS/vendor
 * concentration (DORA), cost MTD, license utilisation, open critical
 * incidents, vendor outages affecting us, and the next regulation
 * deadline. Pure UI composition over existing read endpoints.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Briefcase, Building2, FileText, Send, LayoutGrid,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface VendorRow { vendor: string; share_pct?: number; assets?: number }
interface Correlation { id: string; severity?: string; title?: string; rule_name?: string; created_at?: string }
interface Proposal { id: string; title?: string; state?: string }
interface RegTimer { id: string; framework?: string; due_at?: string }

export default function CioLandingPage() {
  const [refreshing, setRefreshing]     = useState(false)
  const [vendors, setVendors]           = useState<VendorRow[]>([])
  const [costMtd, setCostMtd]           = useState<number | null>(null)
  const [licensePct, setLicensePct]     = useState<number | null>(null)
  const [openCriticalInc, setCritInc]   = useState<number>(0)
  const [vendorOutages, setVendorOutages] = useState<Correlation[]>([])
  const [regTimers, setRegTimers]       = useState<RegTimer[]>([])
  const [proposals, setProposals]       = useState<Proposal[]>([])
  const [proposalsByState, setPropByState] = useState<Record<string, number>>({})

  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cdm/vendor-concentration', { params: { top_n: 10 } })),
      j(api.get('/api/executive-summary')),
      j(api.get('/api/cdm/correlations', { params: { rule: 'vendor_outage_blast_radius', state: 'open', limit: 5 } })),
      j(api.get('/api/incidents', { params: { severity: 'critical', state: 'open', limit: 1 } })),
      j(api.get('/api/cdm/regulation-timers', { params: { state: 'pending_initial' } })),
      j(api.get('/api/cdm/changes/proposals', { params: { limit: 50 } })),
    ]).then(([v, e, o, i, t, c]) => {
      const vendorList = (v.data?.vendors || v.data?.results || []) as VendorRow[]
      setVendors(vendorList)
      const exec = e.data?.rollup || e.data || {}
      setCostMtd(exec?.cost_mtd ?? exec?.cost_mtd_usd ?? null)
      setLicensePct(exec?.license_utilisation_pct ?? exec?.license_pct ?? null)
      setVendorOutages(o.data?.correlations || o.data?.results || [])
      setCritInc(i.data?.open ?? i.data?.count ?? 0)
      setRegTimers(t.data?.timers || t.data?.results || [])
      const props = (c.data?.proposals || c.data?.results || []) as Proposal[]
      setProposals(props)
      const byState: Record<string, number> = {}
      for (const p of props) byState[p.state || 'unknown'] = (byState[p.state || 'unknown'] || 0) + 1
      setPropByState(byState)
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const sendDailyReport = async () => {
    setRefreshing(true)
    try { await api.post('/api/cdm/report/send').catch(() => {}) }
    finally { setRefreshing(false) }
  }

  const topVendor = vendors[0]
  // Days to next regulation deadline — closest non-overdue.
  const sortedTimers = [...regTimers].filter(t => t.due_at)
    .sort((a, b) => new Date(a.due_at!).getTime() - new Date(b.due_at!).getTime())
  const nextDeadline = sortedTimers[0]
  const daysToDeadline = nextDeadline?.due_at
    ? Math.max(0, Math.round((new Date(nextDeadline.due_at).getTime() - Date.now()) / 86_400_000))
    : null

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="CIO Landing"
        subtitle="portfolio · vendors · cost · resilience · regulation"
        persona="cio"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="Top-1 vendor share"
          value={topVendor?.share_pct != null ? `${topVendor.share_pct.toFixed(0)}%` : '—'}
          tone={(topVendor?.share_pct ?? 0) > 40 ? 'rose' : (topVendor?.share_pct ?? 0) > 25 ? 'amber' : 'emerald'}
          sub={topVendor?.vendor || 'no concentration data'} to="/app/cdm" />
        <HeroTile label="Cost MTD"
          value={costMtd != null ? `$${(costMtd / 1000).toFixed(1)}k` : '—'}
          tone={costMtd == null ? 'muted' : 'cyan'} to="/app/savings" />
        <HeroTile label="License util"
          value={licensePct != null ? `${licensePct.toFixed(0)}%` : '—'}
          tone={licensePct == null ? 'muted' : licensePct >= 90 ? 'amber' : licensePct >= 70 ? 'emerald' : 'cyan'}
          to="/app/executive" />
        <HeroTile label="Critical incidents" value={safeNum(openCriticalInc)}
          tone={openCriticalInc > 0 ? 'rose' : 'emerald'} to="/app/incidents" />
        <HeroTile label="Vendor outages affecting us" value={safeNum(vendorOutages.length)}
          tone={vendorOutages.length > 0 ? 'amber' : 'emerald'} to="/app/cdm/correlations" />
        <HeroTile label="Days to next reg"
          value={daysToDeadline != null ? `${daysToDeadline}d` : '—'}
          tone={daysToDeadline == null ? 'muted' : daysToDeadline < 7 ? 'rose' : daysToDeadline < 30 ? 'amber' : 'emerald'}
          sub={nextDeadline?.framework} to="/app/cdm/regulation-timers" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="Top 5 vendor concentration" viewAllTo="/app/cdm">
              {vendors.length === 0 ? (
                <EmptyState message="Vendor concentration not computed."
                  configureTo="/app/cdm/adapters" configureLabel="configure adapters" />
              ) : (
                <div className="space-y-1.5">
                  {vendors.slice(0, 5).map(v => (
                    <div key={v.vendor} className="text-[11px]">
                      <div className="flex justify-between">
                        <span className="text-text-primary truncate">{v.vendor}</span>
                        <span className="font-mono text-text-secondary">{(v.share_pct ?? 0).toFixed(1)}%</span>
                      </div>
                      <div className="h-1.5 bg-surface-raised rounded-full overflow-hidden">
                        <div className="h-full bg-blue/60" style={{ width: `${Math.min(100, v.share_pct ?? 0)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Recent vendor outages affecting us" viewAllTo="/app/cdm/correlations">
              {vendorOutages.length === 0 ? (
                <EmptyState message="No vendor-outage blast-radius alerts."
                  configureTo="/app/cdm/correlation-rules" configureLabel="manage rules" />
              ) : (
                <div className="space-y-1">
                  {vendorOutages.slice(0, 5).map(c => (
                    <ListRow key={c.id}
                      to="/app/cdm/correlations"
                      primary={c.title || c.rule_name || c.id}
                      secondary={c.created_at}
                      right={<span className={c.severity === 'critical' ? 'text-rose-light' : 'text-amber-light'}>
                        {c.severity || 'open'}
                      </span>} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="ChangeProposal portfolio · by state" viewAllTo="/app/cdm/changes">
              {proposals.length === 0 ? (
                <EmptyState message="No ChangeProposals yet." />
              ) : (
                <div className="space-y-1.5">
                  {Object.entries(proposalsByState)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 6)
                    .map(([state, count]) => (
                      <div key={state} className="flex items-center justify-between text-[11px]">
                        <span className="text-text-primary">{state}</span>
                        <span className="font-mono text-text-secondary">{count}</span>
                      </div>
                    ))}
                </div>
              )}
            </Panel>

            <Panel title="Cost MTD" viewAllTo="/app/savings">
              {costMtd == null ? (
                <EmptyState message="No cost data yet."
                  configureTo="/app/connectors-manage" configureLabel="configure cost connector" />
              ) : (
                <div className="text-center py-3">
                  <div className="text-2xl font-bold font-mono text-emerald">
                    ${(costMtd / 1000).toFixed(1)}k
                  </div>
                  <div className="text-[10px] text-text-muted mt-1">spend month-to-date</div>
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'Send daily report',     icon: Send,        tone: 'primary',   onClick: sendDailyReport },
        { label: 'Executive summary',     icon: LayoutGrid,  tone: 'secondary', to: '/app/executive' },
        { label: 'Vendor concentration',  icon: Building2,   tone: 'ghost',     to: '/app/cdm' },
        { label: 'Daily report page',     icon: FileText,    tone: 'ghost',     to: '/app/cdm/report' },
      ]} />

      <span className="hidden"><Briefcase /></span>
    </div>
  )
}
